import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';
import closeIcon from '../../../../../assets/images/close.png';

interface TabItem {
  title: string;
  permission?: string;
  order: number;
}

interface TabModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (tabData: { title: string; permission: string }) => void;
  onRemove?: () => void;
  tabToEdit?: TabItem | null;
  mode: 'add' | 'edit';
}

const TabModal: React.FC<TabModalProps> = ({
  isOpen,
  toggle,
  onSave,
  onRemove,
  tabToEdit,
  mode
}) => {
  const [formData, setFormData] = useState({
    title: '',
    permission: 'All'
  });
  const [hasChanges, setHasChanges] = useState(false);

  const [errors, setErrors] = useState({
    title: ''
  });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && tabToEdit) {
      let mappedPermission = 'All';
      if (tabToEdit.permission === 'Faculty') {
        mappedPermission = 'Program Team Only';
      } else if (tabToEdit.permission === 'None') {
        mappedPermission = 'No One';
      } else if (tabToEdit.permission === 'All') {
        mappedPermission = 'All';
      }
      
      setFormData({
        title: tabToEdit.title,
        permission: mappedPermission
      });
    } else {
      setFormData({
        title: '',
        permission: 'All'
      });
    }
    setErrors({ title: '' });
  }, [mode, tabToEdit, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    setHasChanges(true);
    // Clear error when user starts typing
    if (name === 'title' && errors.title) {
      setErrors(prev => ({
        ...prev,
        title: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = { title: '' };
    
    if (!formData.title.trim()) {
      newErrors.title = 'Tab name is required';
    } else if (formData.title.trim().length < 2) {
      newErrors.title = 'Tab name must be at least 2 characters';
    }
    
    setErrors(newErrors);
    return !newErrors.title;
  };

  const handleSave = () => {
    if (validateForm()) {
      let mappedPermission = 'All';
      if (formData.permission === 'Program Team Only') {
        mappedPermission = 'Faculty';
      } else if (formData.permission === 'No One') {
        mappedPermission = 'None';
      } else if (formData.permission === 'All') {
        mappedPermission = 'All';
      }
      
      onSave({
        title: formData.title.trim(),
        permission: mappedPermission
      });
      toggle();
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', permission: 'All' });
    setErrors({ title: '' });
    toggle();
  };

  const handleRemove = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = () => {
    if (onRemove) {
      onRemove();
      toggle();
    }
    setShowConfirmDialog(false);
  };

  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {mode === 'add' ? 'Add Tab' : 'Edit Tab'}
          </h2>
          <button className={styles.closeButton} onClick={handleCancel}>
            <img src={closeIcon} alt="Close" className={styles.closeIcon} />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.formGroup}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                name="title"
                id="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
                placeholder=" "
              />
              <label htmlFor="title" className={styles.label}>
                Tab Name
              </label>
            </div>
            {errors.title && (
              <div className={styles.errorMessage}>{errors.title}</div>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.permissionLabel}>
              Permission
            </label>
            <div className={styles.radioGroup}>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="permission"
                  value="All"
                  checked={formData.permission === 'All'}
                  onChange={handleInputChange}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>All</span>
              </label>
              
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="permission"
                  value="Program Team Only"
                  checked={formData.permission === 'Program Team Only'}
                  onChange={handleInputChange}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>Program Team Only</span>
              </label>
              
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="permission"
                  value="No One"
                  checked={formData.permission === 'No One'}
                  onChange={handleInputChange}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>No One</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          {mode === 'edit' && onRemove && (
            <button onClick={handleRemove} className={styles.removeButton}>
              Remove
            </button>
          )}
          <button onClick={handleCancel} className={styles.cancelButton}>
            Cancel
          </button>
          <button onClick={handleSave} className={styles.applyButton} disabled={!hasChanges || formData.title.trim() === ''}>
            Apply
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <div className={styles.confirmHeader}>
              <h3 className={styles.confirmTitle}>Remove Tab</h3>
            </div>
            <div className={styles.confirmBody}>
              <p>Are you sure you want to remove the tab "{tabToEdit?.title}"? This action cannot be undone.</p>
            </div>
            <div className={styles.confirmFooter}>
              <button onClick={handleCancelRemove} className={styles.confirmCancelButton}>
                Cancel
              </button>
              <button onClick={handleConfirmRemove} className={styles.confirmRemoveButton}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabModal;

